import { useCallback } from "react";
import { useRouter } from "next/router";
import { useForm, FormProvider } from "react-hook-form";
import { MainTemplate } from "@/components/templates";
import { PageTitle } from "@/components/molecules";
import { useRequestChangeEmailAddressMutation } from "@/generated/graphql";
import { RequestChangeEmailAddressForm } from "@/components/organisms";
import useRollbarUtil from "../hooks/useRollbarUtil";

type FormValues = {
  email: string;
};

type Props = {
  sub: string;
};

// [Note]
// This is the same as the snippet in web/components/pages/RequestChangeEmailAddressPage.tsx.
// The only difference is the title and description.
// useRequestChangeEmailAddressMutation is a mutation to request a change of email address,
// but it is used for two purposes: "change email address" and "resend authentication email".
// -- JP --
// web/components/pages/RequestChangeEmailAddressPage.tsx とほぼ同じコードです。
// ただし、タイトルと説明文が異なります。
// useRequestChangeEmailAddressMutation は、メールアドレスの変更をリクエストするための mutation ですが、
// 「メールアドレス変更」と「認証メール再送信」の二つの用途で使われています。

export const RequestVerifyEmailAddressPage = ({ sub }: Props) => {
  const { getNotifyErrorHandlerForGraphQL } = useRollbarUtil();
  const router = useRouter();

  const methods = useForm<FormValues>({
    reValidateMode: "onBlur",
    defaultValues: {
      email: "",
    },
  });

  const [requestChangeEmailAddressMutation] =
    useRequestChangeEmailAddressMutation({
      onCompleted: (data) => {
        if (data.requestChangeEmailAddress.result) {
          router.push("/request-change-email-address-completed");
        }
      },
      onError: getNotifyErrorHandlerForGraphQL(
        "Failed to send change email address mail",
        "メールの送付に失敗しました"
      ),
    });

  const handleSubmit = useCallback(
    async ({ email }: FormValues) => {
      await requestChangeEmailAddressMutation({
        variables: {
          input: {
            sub,
            email,
          },
        },
      });
    },
    [requestChangeEmailAddressMutation]
  );

  return (
    <MainTemplate title={`メールアドレスの認証`}>
      <PageTitle
        mainTitle={`メールアドレスの認証`}
        subTitle={
          <>
            メールアドレスを認証するためのメールを送信します。
            <br />
            Mikatano IDに登録するメールアドレスを入力してください。
          </>
        }
      />
      <FormProvider {...methods}>
        <RequestChangeEmailAddressForm onSubmit={handleSubmit} />
      </FormProvider>
    </MainTemplate>
  );
};
